import { useCallback, useEffect, useState } from "react";
import { Eye, Send } from "lucide-react";

import { API_V1_ADHOC_EMAIL_REQUESTS } from "../../../config/Endpoints.js";
import { Card } from "../../components/shared/card/card.tsx";
import PageShell from "../../components/shared/page-shell/page-shell.tsx";
import { Badge, Button, Modal, Select, TextArea } from "../../components/ui/index.ts";
import { ResponsiveDataTable, type ResponsiveColumn } from "../../components/ui/responsive-table/responsive-table.tsx";
import { getApi, putApi } from "../../utils/api.ts";
import { accessToken } from "../../utils/auth.ts";
import { showError, showSuccess } from "../../utils/toast.ts";
import { AdhocEmailFilters } from "./adhoc-email-filters.tsx";
import { companyNameLabel, employeeTypeLabel, parseAdhocList, proposedEmail, rowEmployeeName, type AdhocEmailRow } from "./adhoc-email.types.ts";
import styles from "./adhoc-email.module.css";
import { formatCalendarDateDisplay } from "../../../utils/helpers.js";

type HrActionForm = {
  action: string;
  comment: string;
};

type HrActionErrors = Partial<Record<keyof HrActionForm, string>>;

const EMPTY_FORM: HrActionForm = { action: "", comment: "" };
const ACTION_OPTIONS = [
  { value: "APPROVED", label: "Approve" },
  { value: "REJECTED", label: "Reject" },
];

function statusLabel(status?: string | null): string {
  if (!status) return "-";
  return status.toLowerCase().replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function statusVariant(status?: string | null): "success" | "warning" | "danger" | "neutral" {
  const value = String(status ?? "").toUpperCase();
  if (value === "APPROVED") return "success";
  if (value === "REJECTED") return "danger";
  return value === "PENDING" ? "warning" : "neutral";
}

export default function EmailRequestHrApprovalPage() {
  const [rows, setRows] = useState<AdhocEmailRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [loading, setLoading] = useState(false);
  const [draftEmployeeName, setDraftEmployeeName] = useState("");
  const [employeeName, setEmployeeName] = useState("");
  const [draftStatus, setDraftStatus] = useState("PENDING");
  const [status, setStatus] = useState("PENDING");
  const [selected, setSelected] = useState<AdhocEmailRow | null>(null);
  const [form, setForm] = useState<HrActionForm>(EMPTY_FORM);
  const [errors, setErrors] = useState<HrActionErrors>({});
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const query = new URLSearchParams({
        page: String(page - 1),
        size: String(pageSize),
        status,
      });
      if (employeeName.trim()) query.set("employeeName", employeeName.trim());
      const { data } = await getApi<unknown>(`${API_V1_ADHOC_EMAIL_REQUESTS}?${query.toString()}`, accessToken());
      const result = parseAdhocList(data);
      setRows(result.rows);
      setTotal(result.total);
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to load email approval requests.");
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [employeeName, page, pageSize, status]);

  useEffect(() => { void load(); }, [load]);

  const close = () => {
    if (saving) return;
    setSelected(null);
    setForm(EMPTY_FORM);
    setErrors({});
  };

  const update = (key: keyof HrActionForm, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
    setErrors((current) => ({ ...current, [key]: "" }));
  };

  const submit = async () => {
    if (!selected || String(selected.status ?? "").toUpperCase() !== "PENDING") return;
    const nextErrors: HrActionErrors = {};
    if (!form.action) nextErrors.action = "Action is required.";
    if (!form.comment.trim()) nextErrors.comment = "Comment is required.";
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;

    setSaving(true);
    try {
      const payload = { action: form.action, comment: form.comment.trim() };
      const { data } = await putApi<Record<string, unknown>>(`${API_V1_ADHOC_EMAIL_REQUESTS}/${selected.id}/hr-action`, accessToken(), payload);
      showSuccess(String(data?.message ?? "HR action submitted successfully."));
      setSelected(null);
      setForm(EMPTY_FORM);
      setErrors({});
      await load();
    } catch (error) {
      showError(error instanceof Error ? error.message : "Unable to submit HR action.");
    } finally {
      setSaving(false);
    }
  };

  const columns: ResponsiveColumn<AdhocEmailRow>[] = [
    { id: "employee", header: "Name", sortable: true, getSortValue: rowEmployeeName, render: rowEmployeeName, truncate: true, getTitleText: rowEmployeeName },
      {
    id: "companyName",
    header: "Company Name",
    render: (row) => companyNameLabel(row.companyName) || "-",
    truncate: true,
  },
    { id: "personalEmail", header: "Personal Email", accessor: "personalEmail", truncate: true },
    // { id: "proposedEmail", header: "Proposed Email", sortable: true, getSortValue: proposedEmail, render: proposedEmail, truncate: true, getTitleText: proposedEmail },
    // { id: "phone", header: "Phone Number", render: (row) => String(row.contactNo ?? row.phoneNumber ?? "-"), nowrap: true },
     {
      id: "requestedAt",
      header: "Requested On",
      accessor: "createdAt",
      sortable: true,
      render: (row) => formatCalendarDateDisplay(row.createdAt),
      nowrap: true,
    },
    { id: "status", header: "Status", render: (row) => <Badge variant={statusVariant(row.status)}>{statusLabel(row.status)}</Badge>, nowrap: true },
    { id: "action", header: "Action", render: (row) => <Button size="sm" variant="muted-outline" startIcon={<Eye size={15} />} onClick={() => setSelected(row)}>View</Button>, nowrap: true },
  ];

  const detailRows = selected ? [
    ...(selected.status?.trim().toUpperCase() !== "CONFIGURED"
      ? [["First Name", selected.firstName?.trim() || "Not available"], ["Last Name", selected.lastName?.trim() || "Not available"]]
      : []),
    ["Resource Type", employeeTypeLabel(selected.empType ?? selected.employeeType)],
    ["Personal Email", selected.personalEmail],
    ...(selected.status?.trim().toUpperCase() === "CONFIGURED"
      ? [["Configured Email", selected.ccEmail?.trim() || "Not available"]]
      : []),
    ["Proposed Email", proposedEmail(selected)],
    ["Employee Contact Number", selected.contactNumber ?? selected.contactNo ?? selected.phoneNumber],
    ["Company Name", companyNameLabel(selected.companyName)],
    ["Agency Name", selected.agencyName],
    ["Agency Contact Number", selected.agencyContactNumber ?? "-"],
  ].filter(([, value]) => value != null && value !== "" && value !== "-") : [];
  const canTakeAction = String(selected?.status ?? "").toUpperCase() === "PENDING";

  return (
    <PageShell>
      <div className={styles.page}>
        <Card className={styles.filterCard}>
          <AdhocEmailFilters
            employeeName={draftEmployeeName}
            status={draftStatus}
            busy={loading}
            onEmployeeChange={setDraftEmployeeName}
            onStatusChange={setDraftStatus}
            onApply={() => { setEmployeeName(draftEmployeeName); setStatus(draftStatus); setPage(1); }}
            onClear={() => { setDraftEmployeeName(""); setEmployeeName(""); setDraftStatus("PENDING"); setStatus("PENDING"); setPage(1); }}
          />
        </Card>

        <section className={styles.tablePanel}>
          <ResponsiveDataTable
            columns={columns}
            data={rows}
            serialNumber
            page={page}
            pageSize={pageSize}
            totalItems={total}
            onPageChange={setPage}
            onPageSizeChange={(size) => { setPageSize(size); setPage(1); }}
            totalLabel={`Total Records: ${total}`}
            aria-label="HR email request approvals"
            emptyState={<div className={styles.empty}>{loading ? "Loading requests..." : "No pending requests available"}</div>}
          />
        </section>

        <Modal
          open={selected != null}
          onClose={close}
          title={canTakeAction ? "Email Request Approval" : "Email Request Details"}
          size="lg"
          footer={<div className={styles.modalFooter}><Button variant="light-grey" disabled={saving} onClick={close}>{canTakeAction ? "Cancel" : "Close"}</Button>{canTakeAction ? <Button startIcon={<Send size={16} />} disabled={saving} onClick={() => void submit()}>{saving ? "Submitting..." : "Submit"}</Button> : null}</div>}
        >
          {selected ? (
            <div className={styles.configurationModal}>
              <dl className={styles.configurationSummary}>
                {detailRows.map(([label, value]) => <div key={String(label)}><dt>{label}</dt><dd>{String(value)}</dd></div>)}
              </dl>
              {canTakeAction ? <div className={styles.formGrid}>
                <Select label="Action" required value={form.action} options={ACTION_OPTIONS} placeholder="Select Action" error={errors.action} onChange={(value) => update("action", value)} />
                <div className={styles.fullWidth}>
                  <TextArea id="hr-action-comment" label="Comment" required rows={3} maxLength={250} showCharacterCount value={form.comment} error={errors.comment} placeholder="Enter Comment" onChange={(event) => update("comment", event.target.value)} />
                </div>
              </div> : null}
            </div>
          ) : null}
        </Modal>
      </div>
    </PageShell>
  );
}
